import React from 'react';
import { MdReceipt, MdLocationOn, MdVerifiedUser } from 'react-icons/md';
import ReactPaginate from 'react-paginate';
import TableLoadingWrapper from './TableLoadingWrapper';
import '../../styles/views.css';

const ReceiptsView = ({
  pagedFilteredData,
  searchTerm,
  setSearchTerm,
  pageCount,
  handlePageClick,
  loading,
  userRole,
  userMmdaId,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getValidationStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'validated':
      case 'valid':
        return { bg: '#e8f5e8', color: '#2e7d32' };
      case 'invalid':
      case 'rejected':
        return { bg: '#ffebee', color: '#c62828' };
      case 'pending':
        return { bg: '#fff3cd', color: '#856404' };
      default:
        return { bg: '#f5f5f5', color: '#666' };
    }
  };

  return (
    <>
      {/* Header */}
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isMobile ? '12px' : '0',
      }}>
        <input
          type="text"
          placeholder="Search regional receipts..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            width: isMobile ? '100%' : 300,
            padding: '8px 12px',
            borderRadius: 4,
            border: '1px solid #ccc',
            fontSize: isMobile ? 14 : 16,
            outline: 'none',
          }}
        />
        
        <div style={{
          padding: '10px 16px',
          backgroundColor: '#e8f5e8',
          color: '#2e7d32',
          border: '1px solid #c8e6c9',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 'bold',
          width: isMobile ? '100%' : 'auto',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          Regional Receipt Management
        </div>
      </div>

      {/* Receipts Table */}
      <TableLoadingWrapper loading={loading}>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Receipt Number</th>
                <th>Amount</th>
                <th>Transaction ID</th>
                <th>Zone</th>
                <th>Revenue Officer</th>
                <th>Validation Status</th>
                <th>Issue Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedFilteredData.map((row, i) => {
                const validationStyle = getValidationStatusColor(row.validation_status);
                return (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdReceipt size={16} style={{ color: '#ff9800' }} />
                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>
                          {row.receipt_number || `RCP-${row.id}`}
                        </span>
                      </div>
                    </td>
                    <td>
                      <strong style={{ color: '#2e7d32', fontSize: 14 }}>
                        GH₵ {parseFloat(row.amount || 0).toFixed(2)}
                      </strong>
                    </td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {row.transaction_id || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MdLocationOn size={14} style={{ color: '#1976d2' }} />
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#e3f2fd',
                          borderRadius: 4,
                          fontSize: 12,
                          color: '#1976d2'
                        }}>
                          {row.zone_name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td>{row.officer_name || 'N/A'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MdVerifiedUser size={14} style={{ color: validationStyle.color }} />
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 'bold',
                          backgroundColor: validationStyle.bg,
                          color: validationStyle.color
                        }}>
                          {(row.validation_status || 'pending').charAt(0).toUpperCase() + 
                           (row.validation_status || 'pending').slice(1)}
                        </span>
                      </div>
                    </td>
                    <td>{new Date(row.issue_date || row.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '4px' : '8px',
                        alignItems: isMobile ? 'stretch' : 'center'
                      }}>
                        <button
                          onClick={() => console.log('View receipt:', row)}
                          style={{
                            padding: isMobile ? '6px 8px' : '6px 12px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: isMobile ? 11 : 12,
                            width: isMobile ? '100%' : 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => console.log('Download receipt:', row)}
                          style={{
                            padding: isMobile ? '6px 8px' : '6px 12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: isMobile ? 11 : 12,
                            width: isMobile ? '100%' : 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          Download
                        </button>
                        {row.validation_status === 'pending' && (
                          <button
                            onClick={() => console.log('Validate receipt:', row)}
                            style={{
                              padding: isMobile ? '6px 8px' : '6px 12px',
                              backgroundColor: '#ff9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: isMobile ? 11 : 12,
                              width: isMobile ? '100%' : 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            Validate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableLoadingWrapper>

      {/* Pagination */}
      {pageCount > 1 && (
        <ReactPaginate
          previousLabel={'previous'}
          nextLabel={'next'}
          breakLabel={'...'}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={'pagination'}
          activeClassName={'active'}
        />
      )}

      {/* Receipt Management Info */}
      <div style={{
        marginTop: 20,
        padding: 15,
        backgroundColor: '#e8f5e8',
        borderRadius: 8,
        border: '1px solid #c8e6c9',
        fontSize: 14,
        color: '#2e7d32'
      }}>
        <strong>Regional Receipt Management:</strong> Track and validate receipts generated within your MMDA region. 
        Ensure proper documentation and validation of all revenue collection activities.
      </div>
    </>
  );
};

export default ReceiptsView;
